import { useMemo } from 'react';
import {
  NoShowRateByType,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsHooks, ChartCTA, InfoTipPopover } from '@frontend/analytics-shared';
import { AreaChartData } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { Text } from '@frontend/design-system';
import { nsrTimePeriods } from '../components/filters';
import { NSRTypeComparisonText } from '../components/nsr-type-comparison-text';
import { getTimelineValuePair, useFetchNSRROITimeline } from '../hooks/use-fetch-nsr-roi-timeline';
import { useNSRRoiShallowStore } from '../hooks/use-nsr-roi-store';
import { NSRRoiTimelineChartBase } from './nsr-roi-timeline-chart-base';
import { CommonChartProps } from './types';

export const NSRRoiConfirmationStatusTimelineChart = (props: CommonChartProps) => {
  const { t } = useTranslation('analytics');
  const { filters } = useNSRRoiShallowStore('filters');

  const timePeriod = useMemo(() => {
    return filters?.startDate && filters?.endDate
      ? AnalyticsHooks.findExistingTimePeriodKey<typeof nsrTimePeriods>(
          filters.startDate,
          filters.endDate,
          nsrTimePeriods
        ) ?? undefined
      : undefined;
  }, [filters?.startDate, filters?.endDate]);

  const { aggregates, isLoading, rawData } = useFetchNSRROITimeline({
    additionalRequestPayload: {
      roiType: RoiType.WEAVE,
      noShowRateType: NoShowRateByType.APPOINTMENTS_CONFIRMED,
    },
    filters,
  });

  const { highlight, description } = useMemo(() => {
    const unconfirmed = Math.round(aggregates?.['Unconfirmed'] || 0);
    const confirmed = Math.round(aggregates?.['Confirmed'] || 0);

    if (unconfirmed > confirmed) {
      return {
        highlight: t('Unconfirmed Appointments'),
        description: t('Send additional reminders for unconfirmed appointments to reduce no-shows'),
      };
    }

    if (confirmed > unconfirmed) {
      return {
        highlight: t('Confirmed Appointments'),
        description: t('Send additional reminders for confirmed appointments to reduce no-shows'),
      };
    }

    if (confirmed === 0 && confirmed === unconfirmed) {
      return {
        highlight: t('NA'),
        description: t('No appointment data available to determine confirmation status no-show rates'),
      };
    }

    return {
      highlight: t('Confirmed & Unconfirmed Appointments'),
      description: t('Send additional reminders for all appointments to reduce no-shows'),
    };
  }, [aggregates, t]);

  const chartData = useMemo<AreaChartData>(() => {
    return {
      groups:
        rawData?.data?.dates?.map(({ key = '', params = [] }) => {
          const valuePair = getTimelineValuePair(params);

          return {
            name: key,
            values: {
              confirmed: valuePair['Confirmed'] ?? 0,
              unconfirmed: valuePair['Unconfirmed'] ?? 0,
            },
          };
        }) || [],
    };
  }, [rawData]);

  return (
    <NSRRoiTimelineChartBase
      {...props}
      afterChart={
        <ChartCTA
          buttonLabel={t('Send Reminder')}
          ctaType='sendReminders'
          title={t('Highest No-Show')}
          highlight={highlight}
          description={description}
        />
      }
      chartData={chartData}
      infoTip={<InfoTipPopover>{t('Track no-shows based on the appointment confirmation status')}</InfoTipPopover>}
      isLoading={isLoading}
      legendValues={{
        confirmed: formatters.appendPercent(aggregates?.['Confirmed'] || 0),
        unconfirmed: formatters.appendPercent(aggregates?.['Unconfirmed'] || 0),
      }}
      tooltipBottomContentRenderer={(data) => (
        <Text color='subdued' size='small' style={{ maxWidth: '230px' }}>
          <NSRTypeComparisonText
            forTooltip
            formerMetric={t('Confirmed appointments')}
            formerValue={data.confirmed}
            latterMetric={t('Unconfirmed appointments')}
            latterValue={data.unconfirmed}
            timePeriod={timePeriod}
          />
        </Text>
      )}
      showLegends
      title={t('No-Show Rate by Confirmation Status')}
      trackingIdBase='no-show-rate-confirmation-status'
    />
  );
};
